import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Container, Stack, Typography, Divider, Chip, LinearProgress,
    Table, TableHead, TableBody, TableRow, TableCell, Alert, IconButton, Tooltip, Snackbar, Button,
    FormControlLabel, Checkbox, Grid
} from '@mui/material'
import { ArrowBack, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from 'axios';

import DocRow from './components/DocRow';
import { MONTHS_FR } from './utils/months';
import { useAuth } from '../../../../contexts/AuthContext';
import useQuery from './utils/useQuery';

export default function IntegrationComptable() {
    const navigate = useNavigate();
    const {clientId, type, year, month} = useQuery();
    const {loading: authLoading, isAuthenticated} = useAuth();

    const asType = useCallback((t = '') => (String(t).trim() === 'emis' ? 'emis' : 'recu'), []);
    const asInt = useCallback((v, def) => {
        const n = Number(v);
        return Number.isInteger(n) ? n : def;
    }, []);

    const q = useMemo(() => {
        const y = asInt(year, new Date().getFullYear());
        const m = asInt(month, new Date().getMonth() + 1);
        const t = asType(type);
        return {clientId: String(clientId || ''), type: t, year: y, month: m};
    }, [clientId, type, year, month, asInt, asType]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [err, setErr] = useState('');
    const [snack, setSnack] = useState({open: false, msg: '', severity: 'success'});
    const [refreshKey, setRefreshKey] = useState(0);
    const [includeIntegrated, setIncludeIntegrated] = useState(false);
    const [accountsByDoc, setAccountsByDoc] = useState({});

    const title = useMemo(() => {
        const period = `${MONTHS_FR[q.month - 1] || `Mois ${q.month}`} ${q.year}`;
        const who = data?.client?.raisonSociale || `${data?.client?.prenom || ''} ${data?.client?.nom || ''}`.trim();
        const typeLabel = q.type === 'emis' ? 'ÉMIS' : 'REÇUS';
        return `Certificats TEJ ${typeLabel} — ${period} — ${who || ''}`;
    }, [data, q.type, q.year, q.month]);

    // Eclater les documents multi-lignes en lignes distinctes
    const expandedRows = useMemo(() => {
        if (!data?.documents?.length) return [];
        const rows = [];
        for (const doc of data.documents) {
            const lignes = doc.lignesRetenue;
            if (lignes && lignes.length > 1) {
                lignes.forEach((ligne, idx) => {
                    rows.push({
                        ...doc,
                        _rowKey: `${doc._id}_ligne_${idx}`,
                        _ligneIndex: idx,
                        _isSubRow: true,
                        _totalLignes: lignes.length,
                        retenue: {
                            ...doc.retenue,
                            natureOperation: ligne.natureOperation || '',
                            baseHT: ligne.baseHT || '',
                            tvaDue: ligne.tvaDue || '',
                            totalTTC: ligne.totalTTC || '',
                            tvaRetenueSource: ligne.tvaRetenueSource || '',
                            tauxRetenue: ligne.tauxRetenue || '',
                            montantRetenue: ligne.montantRetenue || '',
                            montantServi: ligne.montantServi || '',
                        },
                    });
                });
            } else {
                rows.push({ ...doc, _rowKey: doc._id, _ligneIndex: 0, _isSubRow: false, _totalLignes: 1 });
            }
        }
        return rows;
    }, [data]);

    const count = data?.count || 0;
    // Format date en FR
    const formatDate = (value) => {
        if (!value) return '—';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value; // au cas où ce n'est pas un ISO valide
        return d.toLocaleDateString('fr-FR');
    };

    const paymentPeriod = useMemo(() => {
        if (!data?.documents?.length) return {min: null, max: null};

        const dates = data.documents
            .map(d => d.paymentDate)
            .filter(Boolean)
            .map(d => new Date(d))
            .filter(d => !Number.isNaN(d.getTime()));

        if (!dates.length) return {min: null, max: null};

        const min = new Date(Math.min(...dates));
        const max = new Date(Math.max(...dates));
        return {min, max};
    }, [data]);

    const receptionPeriod = useMemo(() => {
        if (!data?.documents?.length) return {min: null, max: null};

        const dates = data.documents
            .map(d => d.downloadedAt)
            .filter(Boolean)
            .map(d => new Date(d))
            .filter(d => !Number.isNaN(d.getTime()));

        if (!dates.length) return {min: null, max: null};

        const min = new Date(Math.min(...dates));
        const max = new Date(Math.max(...dates));
        return {min, max};
    }, [data]);


    const load = useCallback(async (params, signal) => {
        setLoading(true);
        setErr('');
        try {
            const {data: json} = await axios.get('/api/comptable/accounting/integration-docs', {
                params, signal
            });

            const accounts = {};
            await Promise.all(
                json.documents.map(async (doc) => {
                    try {
                        const lignes = doc.lignesRetenue;
                        if (lignes && lignes.length > 1) {
                            // Multi-lignes : charger suggestion par taux
                            for (let idx = 0; idx < lignes.length; idx++) {
                                const rowKey = `${doc._id}_ligne_${idx}`;
                                try {
                                    const {data: rule} = await axios.get(`/api/comptable/accounting/rules/${params.clientId}/${doc._id}`, {
                                        params: { ligneIndex: idx },
                                        signal
                                    });
                                    if (rule?.debitCode || rule?.creditCode) {
                                        accounts[rowKey] = { debit: rule.debitCode || null, credit: rule.creditCode || null };
                                    }
                                } catch {}
                            }
                        } else {
                            const {data: rule} = await axios.get(`/api/comptable/accounting/rules/${params.clientId}/${doc._id}`, {signal});
                            if (rule?.debitCode || rule?.creditCode) {
                                accounts[doc._id] = { debit: rule.debitCode || null, credit: rule.creditCode || null };
                            }
                        }
                    } catch (e) {
                        console.error(`Erreur lors du chargement des règles pour le document ${doc._id}:`, e.message);
                    }
                })
            );

            setData(json);
            setAccountsByDoc(accounts);
        } catch (e) {
            if (e.name !== 'CanceledError') {
                setErr(e?.response?.data?.error || e.message);
            }
        } finally {
            setLoading(false);
        }
    }, []);
    const handleBackToDocuments = useCallback(() => {
        const params = new URLSearchParams({
            clientId: q.clientId,
            type: q.type,
            year: String(q.year),
            month: String(q.month),
        });

        navigate(`/dashboard/documents/classement?${params.toString()}`);
    }, [navigate, q.clientId, q.type, q.year, q.month]);

    useEffect(() => {
        if (authLoading || !isAuthenticated) return;
        const ctrl = new AbortController();
        load(q, ctrl.signal);
        return () => ctrl.abort();
    }, [authLoading, isAuthenticated, load, q, refreshKey]);

    const handleAccountsChange = useCallback((rowKey, patch) => {
        setAccountsByDoc(prev => ({...prev, [rowKey]: {...prev[rowKey], ...patch}}));
    }, []);

    const allFilled = useMemo(() => {
        if (!expandedRows.length) return false;
        const rowsToCheck = includeIntegrated ? expandedRows : expandedRows.filter(d => !d.isIntegrated);
        return rowsToCheck.every(d => !!accountsByDoc[d._rowKey]?.debit && !!accountsByDoc[d._rowKey]?.credit);
    }, [expandedRows, accountsByDoc, includeIntegrated]);

    const sanitize = (s = '') =>
        String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^\w\-]+/g, '_').replace(/_+/g, '_');

    const handleExport = useCallback(async () => {
        if (!expandedRows.length) return;

        const rowsToExport = includeIntegrated ? expandedRows : expandedRows.filter(d => !d.isIntegrated);
        if (!rowsToExport.length) {
            setSnack({
                open: true,
                msg: includeIntegrated ? 'Aucun document à exporter.' : 'Aucun document non intégré à exporter.',
                severity: 'warning'
            });
            return;
        }

        const missing = rowsToExport.filter(d => !accountsByDoc[d._rowKey]?.debit || !accountsByDoc[d._rowKey]?.credit);
        if (missing.length) {
            setSnack({
                open: true,
                msg: `Complétez Débit/Crédit pour ${missing.length} ligne(s).`,
                severity: 'error'
            });
            return;
        }

        setExporting(true);
        try {
            // Sauvegarder les regles + marquer integres (une seule fois par docId)
            const savedDocIds = new Set();
            await Promise.all(
                rowsToExport.map(d => {
                    const acc = accountsByDoc[d._rowKey];
                    const shouldUpdateIntegrated = !d.isIntegrated && !savedDocIds.has(d._id);
                    if (shouldUpdateIntegrated) savedDocIds.add(d._id);
                    return axios.post('/api/comptable/accounting/rules', {
                        clientId: q.clientId,
                        docId: d._id,
                        debitCode: acc.debit,
                        creditCode: acc.credit,
                        ligneIndex: d._isSubRow ? d._ligneIndex : undefined,
                        tauxRetenue: d?.retenue?.tauxRetenue || undefined,
                        natureOperation: d?.retenue?.natureOperation || undefined,
                        isIntegrated: shouldUpdateIntegrated
                    });
                })
            );

            const rows = rowsToExport.map(d => {
                const acc = accountsByDoc[d._rowKey];
                const contrepartie = d?.contrepartie?.nomOuRaison || '';
                const mf = d?.contrepartie?.matriculeFiscal || '';
                const natureOp = d?.retenue?.natureOperation ?? '';
                const tauxRS = d?.retenue?.tauxRetenue ?? '';
                const baseHT = d?.retenue?.baseHT ?? '';
                const tvaDue = d?.retenue?.tvaDue ?? '';
                const totalTTC = d?.retenue?.totalTTC ?? '';
                const montantServi = d?.retenue?.montantServi ?? '';
                const montantRetenue = d?.retenue?.montantRetenue ?? '';
                const tvaRS = d?.retenue?.tvaRetenueSource ?? '';
                const date = d?.paymentDate || '';

                return {
                    Date: date,
                    Client: contrepartie,
                    MF: mf,
                    'Nature operation': natureOp,
                    'Base HT': baseHT,
                    'TVA due': tvaDue,
                    'Total TTC': totalTTC,
                    'TVA retenue a la source': tvaRS,
                    'Taux de la retenue': tauxRS,
                    'Montant de la retenue': montantRetenue,
                    'Montant servi': montantServi,
                    'Compte Debite': acc.debit,
                    'Compte Credite': acc.credit,
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, 'Integration');

            const clientName = data?.client?.raisonSociale || `${data?.client?.prenom || ''} ${data?.client?.nom || ''}`.trim();
            const fname = `${sanitize(clientName)}_${sanitize(String(q.type))}_${q.year}-${String(q.month).padStart(2, '0')}.xlsx`;
            XLSX.writeFile(wb, fname);

            setSnack({
                open: true,
                msg: 'Règles enregistrées, documents intégrés et Excel exporté ✅',
                severity: 'success'
            });
        } catch (e) {
            setSnack({open: true, msg: e?.response?.data?.error || e.message || 'Erreur export', severity: 'error'});
        } finally {
            setExporting(false);
        }
    }, [expandedRows, data, accountsByDoc, q, includeIntegrated]);

    return (
        <Container maxWidth="lg" sx={{py: 4}}>
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    position: 'relative',
                    bgcolor: 'background.paper',
                }}
            >
                {loading && (
                    <Box sx={{position: 'absolute', left: 0, right: 0, top: 0}}>
                        <LinearProgress/>
                    </Box>
                )}

                {/* HEADER PRINCIPAL */}
                <Stack spacing={2}>
                    <Stack
                        direction={{xs: 'column', md: 'row'}}
                        justifyContent="space-between"
                        alignItems={{xs: 'flex-start', md: 'center'}}
                        spacing={2}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Tooltip title="Retour aux documents">
                                <IconButton onClick={handleBackToDocuments} color="primary">
                                    <ArrowBack/>
                                </IconButton>
                            </Tooltip>

                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{fontWeight: 700, color: 'text.primary'}}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{mt: 0.25}}
                                >
                                    Intégration comptable des certificats TEJ
                                </Typography>
                            </Box>

                            <Chip
                                label={`${count} document(s)`}
                                size="small"
                                sx={{ml: 1, bgcolor: 'primary.light', color: 'primary.contrastText'}}
                            />
                        </Stack>

                        {/* ACTION PRINCIPALE */}
                        <Tooltip
                            title={
                                allFilled
                                    ? 'Exporter en Excel'
                                    : 'Complétez tous les comptes Débit/Crédit pour les documents sélectionnés'
                            }
                        >
                        <span>
                            <Button
                                startIcon={<Download/>}
                                variant="contained"
                                disabled={!allFilled || exporting || loading}
                                onClick={handleExport}
                                sx={{borderRadius: 2, px: 3}}
                            >
                                Exporter Excel
                            </Button>
                        </span>
                        </Tooltip>
                    </Stack>

                    <Divider/>

                    {/* ZONE D’INFOS + FILTRE */}
                    <Grid
                        container
                        spacing={2}
                        alignItems="flex-start"
                    >
                        <Grid item xs={12} md={4}>
                            <Box sx={{p: 2, borderRadius: 2, bgcolor: 'grey.50'}}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Période de paiement
                                </Typography>
                                <Typography variant="body2" sx={{mt: 0.5}}>
                                    Du <strong>{formatDate(paymentPeriod.min)}</strong> au{' '}
                                    <strong>{formatDate(paymentPeriod.max)}</strong>
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{p: 2, borderRadius: 2, bgcolor: 'grey.50'}}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Période de réception
                                </Typography>
                                <Typography variant="body2" sx={{mt: 0.5}}>
                                    Du <strong>{formatDate(receptionPeriod.min)}</strong> au{' '}
                                    <strong>{formatDate(receptionPeriod.max)}</strong>
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: {xs: 'flex-start', md: 'flex-end'}
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeIntegrated}
                                        onChange={(e) => setIncludeIntegrated(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Inclure les documents intégrés"
                            />
                        </Grid>
                    </Grid>

                    {/* ERREUR */}
                    {err && (
                        <Alert severity="error" sx={{mt: 1, borderRadius: 2}}>
                            {err}
                        </Alert>
                    )}

                    {/* TABLEAU / ÉTATS */}
                    <Box sx={{mt: 2}}>
                        {!loading && data && data.documents?.length > 0 && (
                            <Box
                                sx={{
                                    overflowX: 'auto',
                                    borderRadius: 2,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Table
                                    size="small"
                                    stickyHeader
                                    sx={{
                                        minWidth: 700,
                                        '& th': {bgcolor: 'grey.100'},
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 600}}>Client</TableCell>
                                            <TableCell align="right" sx={{fontWeight: 600}}>
                                                Montants
                                            </TableCell>
                                            <TableCell align="center" sx={{fontWeight: 600}}>
                                                Taux RS
                                            </TableCell>
                                            <TableCell sx={{fontWeight: 600}}>
                                                Compte Débité
                                            </TableCell>
                                            <TableCell sx={{fontWeight: 600}}>
                                                Compte Crédité
                                            </TableCell>
                                            <TableCell sx={{fontWeight: 600}}>Intégré</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {expandedRows.map((row) => (
                                            <DocRow
                                                key={row._rowKey}
                                                rowKey={row._rowKey}
                                                clientId={data.client._id}
                                                doc={row}
                                                isSubRow={row._isSubRow}
                                                ligneIndex={row._ligneIndex}
                                                totalLignes={row._totalLignes}
                                                onAccountsChange={handleAccountsChange}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {!loading && data && data.documents?.length === 0 && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 3,
                                    borderRadius: 2,
                                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                                    Aucun document à intégrer pour cette période.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                    Vérifiez les filtres (mois, année, type) ou revenez à la liste des
                                    documents.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Paper>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({...s, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={() => setSnack((s) => ({...s, open: false}))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{width: '100%', borderRadius: 2}}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Container>
    );
}
